/**
 * TPRA Risks API Request Functions & React Query Hooks
 *
 * Handles HTTP requests to the TPRA Dashboard risks endpoint
 * with proper error handling and type safety.
 */

import { get } from '@/infra/rest';
import { APIEndpoints } from '@/shared/constants/Endpoints';
import { risksQueryKeys } from '@/shared/constants/QueryKeys';
import { addNotification } from '@/shared/states/notification';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  RisksQueryParams,
  RisksAPIResponse,
  RiskItem,
} from '../types/risks';
import { transformRisksData } from '../utils/transformRisksData';

/**
 * Sanitizes query parameters by removing undefined/null values
 * Ensures clean API requests without empty parameters
 *
 * @param params - Raw query parameters
 * @returns Sanitized parameters object
 */
function sanitizeQueryParams(
  params: RisksQueryParams
): Record<string, string | number> {
  const sanitized: Record<string, string | number> = {};

  if (params.page !== undefined) {
    sanitized.page = params.page;
  }

  if (params.page_size !== undefined) {
    sanitized.page_size = params.page_size;
  }

  if (params.search && params.search.trim()) {
    sanitized.search = params.search.trim();
  }

  if (params.severity) {
    sanitized.severity = params.severity;
  }

  if (params.sort_by) {
    sanitized.sort_by = params.sort_by;
  }

  if (params.sort_order) {
    sanitized.sort_order = params.sort_order;
  }

  return sanitized;
}

/**
 * Fetches TPRA risks list from the API
 *
 * @param params - Query parameters for filtering, pagination, sorting
 * @returns Risks data with pagination metadata
 * @throws Error if API request fails
 *
 * @example
 * ```typescript
 * const risks = await getRisks({
 *   page: 1,
 *   page_size: 25,
 *   severity: 'High',
 *   search: 'google'
 * });
 * ```
 */
export async function getRisks(
  params: RisksQueryParams
): Promise<Omit<RisksAPIResponse, 'risks'> & { risks: RiskItem[] }> {
  try {
    const response = await get<RisksAPIResponse>({
      url: APIEndpoints.TPRADashboardRisks,
      config: {
        params: sanitizeQueryParams(params),
      },
    });

    // Transform API data to UI format
    const transformed = transformRisksData(response);

    return transformed;
  } catch (error) {
    console.error('Error fetching TPRA risks:', error);
    // Let component handle error display - avoid duplicate notifications
    throw error;
  }
}

/**
 * React Query hook for fetching TPRA risks
 *
 * Provides:
 * - Automatic caching with TanStack Query
 * - Loading and error states
 * - Automatic refetching on mount
 * - Retry logic for failed requests
 * - Placeholder data during refetch
 *
 * @param params - Query parameters for filtering, pagination, sorting
 * @returns React Query result with risks data
 *
 * @example
 * ```typescript
 * function RisksTable() {
 *   const { data, isLoading, isError, error } = useGetRisks({
 *     page: 1,
 *     page_size: 25,
 *     severity: 'High'
 *   });
 *
 *   if (isLoading) return <LoadingSpinner />;
 *   if (isError) return <ErrorMessage error={error} />;
 *
 *   return <Table data={data.risks} />;
 * }
 * ```
 */
export function useGetRisks(
  params: RisksQueryParams
): UseQueryResult<Omit<RisksAPIResponse, 'risks'> & { risks: RiskItem[] }, Error> {
  return useQuery({
    queryKey: risksQueryKeys.list(params),
    queryFn: () => getRisks(params),
    staleTime: 0, // Always refetch for fresh data
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    refetchOnWindowFocus: true, // Refetch when returning to browser tab
    refetchOnMount: true,
    refetchOnReconnect: true,
    placeholderData: (previousData) => previousData,
  });
}
